import { Button, Card } from '@airbus/components-react';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useLanguageContextAPI } from '../../context/LanguageContext';
import styles from '../NotificationTabsPanel.module.css';
import type { NotificationItem } from '../../types';

export type NewNotificationItem = NotificationItem;

export type NewNotificationAction = 'READ' | 'UNREAD' | 'ACCEPT' | 'REJECT' | 'DELETE';

type ActionConfig = {
  allowAccept?: boolean;
  allowReject?: boolean;
  allowDelete?: boolean;
};

type ActionRules = {
  isRead: boolean;
  canToggleRead: boolean;
  showAccept: boolean;
  showReject: boolean;
  showDelete: boolean;
};

type Props = {
  item: NewNotificationItem;
  onAction: (action: NewNotificationAction) => void;
  formatDateTime: (time: string | null) => string;
  disableReadAction?: boolean;
  isProcessing?: boolean;
};

function getCardTitle(
  item: NewNotificationItem,
  t: (key: string, params?: Record<string, unknown>) => string,
): string {
  const { payload, message } = item;
  const messageKey = payload?.messageKey;

  // If no key exists, we can't translate. Fallback immediately.
  if (typeof messageKey !== 'string') {
    return message ?? '';
  }

  const params =
    typeof payload?.messageParams === 'object'
      ? (payload.messageParams as Record<string, unknown>)
      : undefined;

  const translated = t(messageKey, params);

  // If t() returns the key (fallback behavior) or an empty string,
  // we prefer the human-readable item.message.
  return translated && translated !== messageKey ? translated : message ?? '';
}

function getActionRules(item: NewNotificationItem, disableReadAction: boolean): ActionRules {
  const actionConfig = (item.payload as { actionConfig?: ActionConfig } | null)?.actionConfig;
  const status = item.status ?? 'UNREAD';
  const isTerminal = status === 'ACCEPTED' || status === 'REJECTED' || status === 'DELETED';

  return {
    isRead: status !== 'UNREAD',
    canToggleRead: !isTerminal && !disableReadAction,
    showAccept: !isTerminal && actionConfig?.allowAccept !== false,
    showReject: !isTerminal && actionConfig?.allowReject !== false,
    showDelete: !isTerminal && actionConfig?.allowDelete === true,
  };
}

export default function NewNotificationCard({
  item,
  onAction,
  formatDateTime,
  disableReadAction = false,
  isProcessing = false,
}: Props) {
  const { t } = useLanguageContextAPI();
  const rules = getActionRules(item, disableReadAction || isProcessing);
  const readAction: NewNotificationAction = rules.isRead ? 'UNREAD' : 'READ';

  const createdByDisplay = item.createdByName ?? item.createdBy ?? '-';

  const cardTitle = getCardTitle(item, t);
  const readIcon = rules.isRead ? (
    <DraftsOutlinedIcon className={styles.readicon} data-testid='read-notification-icon' />
  ) : (
    <MarkunreadOutlinedIcon className={styles.readicon} />
  );

  return (
    <Card
      className={`${styles.itemCard} ${rules.isRead ? styles.nobar : styles.unread}`}
      key={`new-${item.notificationId ?? item.userNotificationId ?? ''}`}
      style={{ minHeight: 'fit-content' }}
    >
      <div className={styles.itemInner}>
        <div className={styles.itemHeader}>
          <div className={styles.itemTitle}>{cardTitle}</div>
          <div className={styles.headerIcons}>
            {rules.canToggleRead ? (
              <div
                onClick={() => {
                  if (isProcessing) {
                    return;
                  }
                  onAction(readAction);
                }}
              >
                {readIcon}
              </div>
            ) : (
              readIcon
            )}
            {rules.showDelete && (
              <button
                type='button'
                className={styles.deleteIconButton}
                aria-label='Delete notification'
                disabled={isProcessing}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onAction('DELETE');
                }}
              >
                <DeleteOutlineOutlinedIcon className={styles.readicon} />
              </button>
            )}
          </div>
        </div>

        <div className={styles.itemFooter}>
          <span className={styles.timestamp}>
            <span className={styles.createdByText}>{String(createdByDisplay)}</span>
            {' | '}
            {formatDateTime(item.insertDate)}
          </span>
          <div className={styles.actions}>
            {rules.showAccept && (
              <Button
                type='button'
                variant='ghost'
                size='small'
                className={styles.accept}
                onClick={(event) => {
                  event.preventDefault();
                  if (isProcessing) {
                    return;
                  }
                  onAction('ACCEPT');
                }}
              >
                {t('notification_panel.accept')}
              </Button>
            )}
            {rules.showReject && (
              <Button
                type='button'
                variant='ghost'
                size='small'
                className={styles.reject}
                onClick={(event) => {
                  event.preventDefault();
                  if (isProcessing) {
                    return;
                  }
                  onAction('REJECT');
                }}
              >
                {t('notification_panel.reject')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
